import { CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import React, { useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useGetLoggedUser } from "../../shared/hooks/useUser";

const RESTAURANT_CONTEXT = "Restaurant Context";
const ADMIN_REDIRECT_ERROR_PATTERNS = [
    "app restaurant_management is not installed",
    "failed to get method for command restaurant_management",
    "module import failed for restaurant settings",
    "no module named 'frappe.core.doctype.restaurant_settings'",
];

const normalizeImportedContext = (value) => {
    if (value == null) return [];

    if (Array.isArray(value)) {
        return value.map((entry) => String(entry));
    }

    if (typeof value === "object") {
        return Object.keys(value);
    }

    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed) return [];

        try {
            const parsed = JSON.parse(trimmed);
            return normalizeImportedContext(parsed);
        } catch {
            // Fallback for plain/comma-separated values
            return trimmed
                .split(",")
                .map((entry) => entry.trim())
                .filter(Boolean);
        }
    }

    return [];
};

const extractErrorText = (error) => {
    const responseData = error?.response?.data || {};
    const chunks = [
        error?.message,
        responseData?.exception,
        responseData?.exc_type,
    ].filter(Boolean);

    const serverMessagesRaw = responseData?._server_messages;
    if (serverMessagesRaw) {
        try {
            const parsedMessages = JSON.parse(serverMessagesRaw);
            if (Array.isArray(parsedMessages)) {
                parsedMessages.forEach((entry) => {
                    if (typeof entry !== "string") return;
                    try {
                        const nested = JSON.parse(entry);
                        if (nested?.message) chunks.push(nested.message);
                    } catch {
                        chunks.push(entry);
                    }
                });
            }
        } catch {
            chunks.push(String(serverMessagesRaw));
        }
    }

    return chunks.join(" ").toLowerCase();
};

const shouldRedirectToAdminForError = (error) => {
    const text = extractErrorText(error);
    if (!text) return false;
    return ADMIN_REDIRECT_ERROR_PATTERNS.some((pattern) =>
        text.includes(pattern)
    );
};

const ProtectedRoute = ({ children }) => {
    const { data: loggedUser, status } = useGetLoggedUser(true);
    const siteSettingsQuery = useQuery(
        ["SiteSettingsGuard", window.location.origin],
        async () => {
            const params = {
                doctype: "Site Settings",
                name: "Site Settings",
            };
            const response = await axios.get(
                "method/frappe.desk.form.load.getdoc",
                { params }
            );
            return response.data;
        },
        {
            refetchOnMount: true,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            retry: false,
            cacheTime: 0,
            staleTime: 0,
            enabled: status === "success",
        }
    );

    const authResolved = status === "success" || status === "error";
    const loggedIn =
        status === "success" &&
        !!loggedUser &&
        !!loggedUser.user_type &&
        loggedUser.user_type !== "Website User";

    const hasRestaurantContext = useMemo(() => {
        const importedContext =
            siteSettingsQuery?.data?.docs?.[0]?.imported_context;
        const normalized = normalizeImportedContext(importedContext);
        return normalized.includes(RESTAURANT_CONTEXT);
    }, [siteSettingsQuery?.data?.docs]);

    const shouldRedirectForSiteSettingsError = useMemo(() => {
        if (!siteSettingsQuery.isError) return false;
        return shouldRedirectToAdminForError(siteSettingsQuery.error);
    }, [siteSettingsQuery.isError, siteSettingsQuery.error]);

    if (!authResolved) {
        return <CircularProgress />;
    }

    if (!loggedIn) {
        return <Navigate to={"/auth-login"} replace />;
    }

    const isSiteSettingsLoading =
        siteSettingsQuery.isLoading || siteSettingsQuery.isFetching;

    // Avoid premature redirect until Site Settings is definitively loaded.
    if (isSiteSettingsLoading) {
        return <CircularProgress />;
    }

    if (
        shouldRedirectForSiteSettingsError ||
        (!siteSettingsQuery.isError && !hasRestaurantContext)
    ) {
        window.location.replace(`${window.location.origin}/admin`);
        return <CircularProgress />;
    }

    return children;
};

export default ProtectedRoute;
